import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, Camera, X, HardDrive } from "lucide-react";
import { savePhoto, getPhoto, deletePhoto, deleteVisitPhotos, fileToCompressedDataUrl } from "../../lib/localPhotos";

const PHASES = ["Valoración inicial", "Colocación de aparatología", "Alineación", "Nivelación", "Cierre de espacios", "Detallado", "Retención", "Alta"];

function PhotoSlot({ patientId, visitId, slot, label }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  useEffect(() => { getPhoto(patientId, visitId, slot).then(setUrl).catch(() => {}); }, [patientId, visitId, slot]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      await savePhoto(patientId, visitId, slot, dataUrl);
      setUrl(dataUrl);
      toast.success("Foto guardada en este equipo");
    } catch { toast.error("No se pudo guardar la foto"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async () => { await deletePhoto(patientId, visitId, slot); setUrl(null); };

  return (
    <div className="flex-1">
      <p className="text-[11px] font-bold text-brand-ink/50 uppercase tracking-wide mb-1">{label}</p>
      {url ? (
        <div className="relative group">
          <img src={url} alt={label} onClick={() => setZoom(true)} data-testid={`ortho-photo-${slot}-${visitId}`}
            className="w-full h-32 object-cover rounded-xl border border-stone-200 cursor-zoom-in" />
          <button data-testid={`ortho-photo-remove-${slot}-${visitId}`} onClick={remove}
            className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={13} /></button>
          {zoom && (
            <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-6" onClick={() => setZoom(false)}>
              <img src={url} alt={label} className="max-w-full max-h-full rounded-lg" />
            </div>
          )}
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center gap-1 h-32 rounded-xl border-2 border-dashed border-stone-200 hover:border-brand-sage cursor-pointer text-brand-ink/40 hover:text-brand-sage transition-colors" data-testid={`ortho-photo-add-${slot}-${visitId}`}>
          {busy ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
          <span className="text-[11px] font-semibold">{busy ? "Guardando…" : "Agregar foto"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} />
        </label>
      )}
    </div>
  );
}

export default function OrthoTracker({ patient }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), phase: PHASES[0], note: "" });

  const load = async () => {
    setLoading(true);
    try { setVisits((await api.get(`/patients/${patient.patient_id}/ortho`)).data || []); }
    catch { toast.error("No se pudo cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patient.patient_id]);

  const addVisit = async () => {
    setAdding(true);
    try {
      await api.post(`/patients/${patient.patient_id}/ortho`, { date: new Date(form.date).toISOString(), phase: form.phase, note: form.note });
      setForm({ date: new Date().toISOString().slice(0, 10), phase: PHASES[0], note: "" });
      toast.success("Visita registrada ✓"); load();
    } catch { toast.error("No se pudo registrar"); }
    finally { setAdding(false); }
  };
  const del = async (v) => {
    if (!window.confirm("¿Eliminar esta visita y sus fotos locales?")) return;
    await api.delete(`/patients/${patient.patient_id}/ortho/${v.visit_id}`);
    await deleteVisitPhotos(patient.patient_id, v.visit_id);
    load(); toast.success("Eliminado");
  };

  return (
    <div className="space-y-5" data-testid="ortho-panel">
      <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-3 text-[11px] text-blue-800 flex items-center gap-2" data-testid="ortho-local-note">
        <HardDrive size={14} className="shrink-0" /> Las fotos Antes/Después se guardan solo en <b>este equipo</b> (no se suben a la nube). Se verán en esta misma computadora/navegador.
      </div>

      {/* Nueva visita */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="ortho-new-visit">
        <p className="font-outfit font-bold text-brand-ink text-sm mb-3">Registrar visita</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <input data-testid="ortho-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm" />
          <select data-testid="ortho-phase" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })}
            className="px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm bg-white">
            {PHASES.map((p) => <option key={p}>{p}</option>)}
          </select>
          <button data-testid="ortho-add-visit" onClick={addVisit} disabled={adding}
            className="flex items-center justify-center gap-2 font-outfit font-semibold px-4 py-2 rounded-xl bg-brand-terracotta text-white hover:bg-brand-terracottaDark transition-all disabled:opacity-60">
            {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Agregar
          </button>
        </div>
        <textarea data-testid="ortho-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
          placeholder="Notas de la visita (ajustes, arco, indicaciones…)" className="mt-2 w-full px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm" />
      </div>

      {/* Historial de visitas */}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-brand-sage" size={26} /></div>
      ) : visits.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">Aún no hay visitas de ortodoncia registradas.</p>
      ) : (
        <div className="space-y-3" data-testid="ortho-visits">
          {visits.map((v) => (
            <div key={v.visit_id} data-testid={`ortho-visit-${v.visit_id}`} className="bg-white rounded-2xl border border-stone-100 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-sm font-bold text-brand-ink">{new Date(v.date).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}</p>
                  {v.phase && <span className="inline-block mt-1 text-[11px] font-semibold bg-brand-sand/40 text-brand-terracotta rounded-full px-2.5 py-0.5">{v.phase}</span>}
                </div>
                <button data-testid={`ortho-del-${v.visit_id}`} onClick={() => del(v)} className="text-brand-ink/40 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
              </div>
              {v.note && <p className="text-sm text-brand-ink/80 mb-3 whitespace-pre-wrap">{v.note}</p>}
              <div className="flex gap-3">
                <PhotoSlot patientId={patient.patient_id} visitId={v.visit_id} slot="before" label="Antes" />
                <PhotoSlot patientId={patient.patient_id} visitId={v.visit_id} slot="after" label="Después" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
